import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Link, Outlet, useMatches, useRouterState } from '@tanstack/react-router';
import { Fragment, useEffect } from 'react';
import { useAuth } from '@/auth/auth-provider';
import { EnvironmentBanner } from '@/components/environment-banner';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AssistantPageProvider } from '@/lib/assistant/page-context';
import { applyStripedRows, useStripedRows } from '@/lib/table-prefs';
import { cn } from '@/lib/utils';
import type { Crumb } from '../breadcrumbs';
import { environmentInfo } from '../environment';
import { findNavItem, navTrail, resolvePageTitle } from '../nav';
import { useRuntimeConfig } from '../runtime-config-context';
import { AppSidebar } from './app-sidebar';
import { AssistantPanel } from './assistant-panel';
import { CommandPalette, useCommandPalette } from './command-palette';
import { UserMenu } from './user-menu';

/** Section › nav item › the route's own segments (`staticData.crumbs`). The last one is the title. */
function usePageHeading() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const leaf = useMatches({ select: (matches) => matches[matches.length - 1] });
  const { presentation } = useRuntimeConfig();
  const item = findNavItem(pathname);
  const trail = item ? navTrail(item) : undefined;
  const section = trail?.section;
  const fallback = pathname === '/' ? 'Home' : (item?.label ?? 'Not found');
  const itemTitle = resolvePageTitle(pathname, presentation.i18n.pageTitles, fallback);
  const extra = leaf?.staticData.crumbs?.(leaf.params, leaf.loaderData) ?? [];
  // A sub-page (Rounds › Live) links back to its parent page first.
  const parent: Crumb[] = trail?.parent ? [{ label: trail.parent.label, href: trail.parent.to }] : [];
  const crumbs: Crumb[] =
    extra.length > 0 && item
      ? [...parent, { label: itemTitle, href: item.to }, ...extra]
      : [...parent, { label: itemTitle }];
  const documentTitle = [...crumbs]
    .reverse()
    .map((c) => c.label)
    .join(' · ');
  return { section: section?.label, crumbs, title: crumbs[crumbs.length - 1]?.label ?? itemTitle, documentTitle };
}

/** Any in-app path through the splat route; the router still matches the real route. */
const splatLink = (href: string) => ({ to: '/$', params: { _splat: href.replace(/^\//, '') } }) as const;

export function AppShell() {
  const { presentation, backofficeEnv } = useRuntimeConfig();
  const { layout, brand } = presentation;
  const { setOpen } = useCommandPalette();
  const { section, crumbs, title, documentTitle } = usePageHeading();
  const compact = layout.variant === 'compact';
  const topNav = layout.variant === 'top-nav';
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The assistant explains analytics pages, which need VIEW_LIST_GAMES (so does its prompt).
  const assistant = useAuth().can('VIEW_LIST_GAMES');

  useEffect(() => {
    const base = brand.documentTitle || brand.productName;
    document.title = documentTitle ? `${documentTitle} · ${base}` : base;
  }, [documentTitle, brand.documentTitle, brand.productName]);

  const [striped] = useStripedRows();
  useEffect(() => {
    applyStripedRows(striped);
    return () => applyStripedRows(false);
  }, [striped]);

  return (
    <AssistantPageProvider>
      <SidebarProvider>
        <AppSidebar
          variant={layout.variant === 'sidebar-floating' ? 'floating' : 'inset'}
          collapsible={topNav ? 'offcanvas' : (layout.collapsible ?? 'icon')}
        />
        <SidebarInset className="min-w-0">
          <EnvironmentBanner env={environmentInfo(backofficeEnv)} className="rounded-t-[inherit]" />
          <header className={cn('flex shrink-0 items-center gap-2 border-b px-4', compact ? 'h-11' : 'h-14')}>
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-1 !h-4" />
            {layout.showBreadcrumbs !== false && section ? (
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className="hidden md:block">{section}</BreadcrumbItem>
                  {crumbs.map((crumb, i) => (
                    <Fragment key={`${i}-${crumb.label}`}>
                      <BreadcrumbSeparator className={i === 0 ? 'hidden md:block' : undefined} />
                      <BreadcrumbItem>
                        {i < crumbs.length - 1 && crumb.href ? (
                          <BreadcrumbLink asChild>
                            <Link {...splatLink(crumb.href)}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            ) : (
              <span className="text-sm font-medium">{title}</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" className="text-muted-foreground" onClick={() => setOpen(true)}>
                <MagnifyingGlassIcon />
                <span className="hidden sm:inline">Search</span>
                <kbd className="ml-2 hidden rounded border bg-muted px-1.5 font-mono text-[10px] sm:inline">⌘K</kbd>
              </Button>
              <UserMenu />
            </div>
          </header>
          {/* With the floating assistant, room below the page so its button never covers a list's pagination. */}
          <div className={cn('flex-1', compact ? 'p-3 md:p-4' : 'p-4 md:p-6', assistant && 'pb-20!')}>
            <Outlet />
          </div>
        </SidebarInset>
        <CommandPalette />
        {assistant && <AssistantPanel pathname={pathname} pageTitle={title} />}
      </SidebarProvider>
    </AssistantPageProvider>
  );
}
